'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import WorkflowNavigation from '@/components/common/WorkflowNavigation';

interface QuestionnaireAnswers {
  [key: string]: number;
}

interface ResultProfile {
  name: string;
  level: number;
  levelLabel: string;
  color: string;
  bgColor: string;
  borderColor: string;
  interpretation: string;
}

interface Recommendation {
  title: string;
  description: string;
  priority: 'alta' | 'média' | 'baixa';
}

const PROFILES: Record<string, ResultProfile> = {
  'execucao-pressao': {
    name: 'Execução sob Pressão',
    level: 1,
    levelLabel: 'Nível Inicial',
    color: '#DC2626',
    bgColor: '#FEF2F2',
    borderColor: '#FECACA',
    interpretation: 'O seu município opera num contexto de elevada pressão operacional, com processos por consolidar, funções insuficientemente clarificadas e capacidade de execução limitada. A energia organizacional é absorvida pela gestão corrente, deixando pouco espaço para a reorganização interna e para a melhoria estruturada do desempenho. Esta situação é frequente em municípios com estruturas orgânicas desajustadas ou com lacunas de liderança intermédia, e representa uma oportunidade concreta de transformação. A GestPub, através do serviço GP Otimiza, dispõe de metodologia específica para apoiar municípios neste perfil, com uma intervenção pragmática, discreta e orientada para resultados, em reorganização interna, revisão de processos e reforço da capacidade de execução.',
  },
  'estrutura-ajustamento': {
    name: 'Estrutura em Ajustamento',
    level: 2,
    levelLabel: 'Nível de Desenvolvimento',
    color: '#D97706',
    bgColor: '#FFFBEB',
    borderColor: '#FDE68A',
    interpretation: 'O seu município encontra-se numa fase de transição organizacional. Existem bases sólidas em algumas áreas, mas persistem inconsistências na clareza de funções, na revisão de processos e no alinhamento entre a liderança e as equipas operacionais. A vontade de modernizar está presente, mas faltam ainda os instrumentos metodológicos e o acompanhamento especializado para consolidar a transformação de forma estruturada e sustentada. A GestPub, através do serviço GP Otimiza, pode apoiar a definição de um plano de intervenção faseado para alinhar pessoas, processos e estrutura à nova visão política, convertendo essa vontade em resultados mensuráveis.',
  },
  'base-solida': {
    name: 'Base Sólida de Execução',
    level: 3,
    levelLabel: 'Nível Intermédio',
    color: '#1D4ED8',
    bgColor: '#EFF6FF',
    borderColor: '#BFDBFE',
    interpretation: 'O seu município demonstra uma capacidade organizacional consistente, com processos razoavelmente definidos, funções relativamente clarificadas e uma cultura de responsabilização em desenvolvimento. A maioria dos serviços opera com eficiência interna adequada e existe uma visão estratégica partilhada entre os principais dirigentes. O desafio atual reside em elevar o desempenho ao nível de excelência, consolidando as boas práticas, reforçando a gestão estratégica de RH, aprofundando a revisão de processos e expandindo a capacidade de monitorização a toda a organização. A GestPub, através do serviço GP Otimiza, pode apoiar esta fase de consolidação e diferenciação.',
  },
  'maturidade-organizacional': {
    name: 'Maturidade Organizacional',
    level: 4,
    levelLabel: 'Nível Avançado',
    color: '#059669',
    bgColor: '#ECFDF5',
    borderColor: '#A7F3D0',
    interpretation: 'O seu município apresenta um elevado grau de maturidade organizacional. Os processos estão documentados e revistos regularmente, as funções estão clarificadas e formalizadas, a liderança opera de forma estratégica e a capacidade de execução é consistente e mensurável. A gestão de recursos humanos é orientada para o desempenho e a monitorização dos resultados é sistemática. Este nível posiciona o município como referência no setor público português, com capacidade para liderar iniciativas de modernização administrativa e partilha de boas práticas autárquicas. A GestPub, através do serviço GP Otimiza, pode apoiar a consolidação e a disseminação deste modelo de excelência.',
  },
};

const AREA_NAMES = ['Alinhamento Estratégico', 'Reorganização e Processos', 'Liderança e Gestão de Pessoas', 'Execução e Monitorização'];

const AREA_DESCRIPTIONS = [
  'Tradução das prioridades do mandato em objetivos operacionais claros, partilhados e acompanhados',
  'Adequação da estrutura orgânica, revisão de processos internos e clarificação de funções',
  'Qualidade da liderança intermédia, gestão estratégica de RH e desenvolvimento de competências',
  'Capacidade de implementação de decisões, monitorização de resultados e prestação de contas',
];

const RECOMMENDATIONS_BANK: Record<string, Recommendation[]> = {
  'Alinhamento Estratégico': [
    { title: 'Operacionalização das Prioridades do Mandato', description: 'Desenvolver um processo estruturado de desdobramento das prioridades estratégicas em objetivos operacionais mensuráveis por serviço e por equipa, com responsáveis identificados e prazos definidos, assegurando a melhor articulação entre estratégia política e execução técnica.', priority: 'alta' },
    { title: 'Sistema de Alinhamento Vertical de Objetivos', description: 'Implementar uma cascata de objetivos organizacionais, do executivo municipal até às equipas operacionais, garantindo que cada dirigente e colaborador compreende o seu contributo direto para os resultados do mandato e para a modernização administrativa do município.', priority: 'alta' },
    { title: 'Ciclos Regulares de Alinhamento da Liderança', description: 'Estabelecer reuniões estruturadas de alinhamento estratégico entre o executivo e os dirigentes de topo, com foco na coerência das prioridades, na identificação de bloqueios organizacionais e na tomada de decisão orientada para resultados.', priority: 'média' },
  ],
  'Reorganização e Processos': [
    { title: 'Diagnóstico e Reorganização da Estrutura Orgânica', description: 'Avaliar a adequação da estrutura orgânica atual face às necessidades estratégicas do município, identificando sobreposições de competências, duplicação de funções e oportunidades de reorganização interna que melhorem a agilidade, a clareza de responsabilidades e a eficiência interna.', priority: 'alta' },
    { title: 'Mapeamento e Revisão de Processos Críticos', description: 'Identificar, documentar e simplificar os processos internos com maior impacto no serviço ao cidadão e na eficiência operacional, eliminando redundâncias, definindo responsáveis em cada etapa e estabelecendo indicadores de desempenho por processo.', priority: 'alta' },
    { title: 'Programa de Transformação Digital Administrativa', description: 'Priorizar a digitalização dos processos com maior volume e impacto, reduzindo tempos de resposta, aumentando a transparência e libertando capacidade técnica para tarefas de maior valor acrescentado na modernização administrativa dos serviços municipais.', priority: 'média' },
  ],
  'Liderança e Gestão de Pessoas': [
    { title: 'Clarificação Formal de Funções e Responsabilidades', description: 'Rever e formalizar as funções, competências e responsabilidades dos dirigentes intermédios e quadros técnicos, eliminando ambiguidades que comprometem a responsabilização, a eficácia da gestão e a capacidade de execução dos serviços.', priority: 'alta' },
    { title: 'Programa de Desenvolvimento de Liderança Intermédia', description: 'Implementar um programa estruturado de desenvolvimento das competências de liderança para dirigentes intermédios, com foco na gestão de equipas orientada para resultados, na condução de processos de mudança e na melhoria do desempenho organizacional.', priority: 'alta' },
    { title: 'Sistema de Gestão Estratégica de Desempenho', description: 'Reforçar o sistema de avaliação de desempenho com ciclos regulares de feedback, objetivos individuais alinhados com as prioridades municipais e planos de desenvolvimento que retenham, valorizem e mobilizem o talento interno para a modernização do município.', priority: 'média' },
  ],
  'Execução e Monitorização': [
    { title: 'Painel de Monitorização Executiva do Mandato', description: 'Criar um sistema integrado de indicadores de desempenho organizacional que permita ao executivo municipal acompanhar em tempo real o progresso face às prioridades do mandato, identificar desvios com antecedência e tomar decisões corretivas fundamentadas.', priority: 'alta' },
    { title: 'Ciclos de Revisão Operacional por Serviço', description: 'Estabelecer reuniões regulares de revisão operacional com os dirigentes de serviço, centradas na análise de desvios face aos objetivos, na definição de ações corretivas e no reforço da capacidade de execução dentro dos prazos e recursos estabelecidos.', priority: 'alta' },
    { title: 'Cultura Organizacional de Responsabilização pelos Resultados', description: 'Desenvolver mecanismos formais de prestação de contas pelos resultados organizacionais, criando uma cultura em que o cumprimento de compromissos é sistematicamente acompanhado, reconhecido e corrigido, reforçando a credibilidade institucional e a eficiência interna do município.', priority: 'média' },
  ],
};

function calculateResults(answers: QuestionnaireAnswers) {
  const areaScores = [0, 0, 0, 0];
  // q1-q3 → area 0 (Alinhamento Estratégico)
  // q4-q6 → area 1 (Reorganização e Processos)
  // q7-q9 → area 2 (Liderança e Gestão de Pessoas)
  // q10-q12 → area 3 (Execução e Monitorização)
  const questionMap: Record<string, number> = {
    q1: 0, q2: 0, q3: 0,
    q4: 1, q5: 1, q6: 1,
    q7: 2, q8: 2, q9: 2,
    q10: 3, q11: 3, q12: 3,
  };

  for (let q = 1; q <= 12; q++) {
    const key = `q${q}`;
    const answer = answers[key] ?? 3;
    const area = questionMap[key];
    // q5 is reverse scored
    const score = key === 'q5' ? 6 - answer : answer;
    areaScores[area] += score;
  }

  // Each area has 3 questions, max score per area = 15 (3×5), min = 3 (3×1)
  const normalizedScores = areaScores.map(s => Math.round(((s - 3) / 12) * 100));
  const totalScore = normalizedScores.reduce((a, b) => a + b, 0) / 4;

  let profileKey: string;
  if (totalScore < 40) profileKey = 'execucao-pressao';
  else if (totalScore < 60) profileKey = 'estrutura-ajustamento';
  else if (totalScore < 80) profileKey = 'base-solida';
  else profileKey = 'maturidade-organizacional';

  const minScore = Math.min(...normalizedScores);
  const dominantAreaIndex = normalizedScores.indexOf(minScore);

  const sortedAreas = normalizedScores
    .map((score, i) => ({ score, index: i }))
    .sort((a, b) => a.score - b.score);

  const recommendations: Recommendation[] = [];
  for (const area of sortedAreas.slice(0, 3)) {
    const areaName = AREA_NAMES[area.index];
    const recs = RECOMMENDATIONS_BANK[areaName];
    if (recs && recs.length > 0) {
      recommendations.push(recs[0]);
    }
  }

  return {
    profile: PROFILES[profileKey],
    areaScores: normalizedScores,
    totalScore: isNaN(totalScore) ? 0 : totalScore,
    dominantAreaIndex,
    recommendations: recommendations.slice(0, 3),
  };
}

function getMockAnswers(): QuestionnaireAnswers {
  return { q1: 3, q2: 2, q3: 3, q4: 2, q5: 4, q6: 3, q7: 2, q8: 3, q9: 2, q10: 3, q11: 2, q12: 3 };
}

export default function ResultsInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const [results, setResults] = useState<ReturnType<typeof calculateResults> | null>(null);
  const [leadName, setLeadName] = useState('');
  const [municipality, setMunicipality] = useState('');
  const [showShareToast, setShowShareToast] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
    try {
      const savedAnswers = sessionStorage.getItem('questionnaire_answers');
      const savedLead = sessionStorage.getItem('lead_data');
      const answers: QuestionnaireAnswers = savedAnswers ? JSON.parse(savedAnswers) : getMockAnswers();
      const calc = calculateResults(answers);
      setResults(calc);
      if (savedLead) {
        const lead = JSON.parse(savedLead);
        setLeadName(lead.name || '');
        setMunicipality(lead.municipality || '');
        // Show confirmation only once after fresh submission
        if (lead.submissionComplete) {
          setShowConfirmation(true);
          // Clear the flag to prevent showing on refresh
          const updatedLead = { ...lead, submissionComplete: false };
          sessionStorage.setItem('lead_data', JSON.stringify(updatedLead));
        }
      }
    } catch {
      setResults(calculateResults(getMockAnswers()));
    }
  }, []);

  const handleShare = () => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href).then(() => {
        setShowShareToast(true);
        setTimeout(() => setShowShareToast(false), 3000);
      });
    }
  };

  if (!isHydrated || !results) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5F5F5' }}>
        <div className="text-center">
          <div
            className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4"
            style={{ borderColor: '#009688', borderTopColor: 'transparent' }}
          />
          <p style={{ fontFamily: 'DM Sans, sans-serif', color: '#6B7280' }}>A calcular resultados...</p>
        </div>
      </div>
    );
  }

  const { profile, areaScores, totalScore, dominantAreaIndex, recommendations } = results;
  const maturityPercent = isNaN(totalScore) ? 0 : Math.round(totalScore);

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F5F5F5' }}>
      {/* Header — matches reference exactly */}
      <header className="w-full" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        {/* Top bar */}
        <div style={{ backgroundColor: '#272727' }}>
          <div className="max-w-6xl mx-auto px-6 py-2 flex items-center justify-between">
            <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)', letterSpacing: '0.05em' }}>
              Diagnóstico de Capacidade Organizacional Municipal
            </span>
            <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)' }}>
              Resultados
            </span>
          </div>
        </div>
        {/* Main nav */}
        <div className="bg-white border-b border-gray-100">
          <div className="max-w-6xl mx-auto px-6 py-0 flex items-center justify-between">
            <Link href="/landing-page" className="flex items-center py-3 focus-visible:outline-none" aria-label="GestPub — Gestão Pública e Consultoria Autárquica — Página inicial">
              <img
                src="/assets/images/6-1773621431219.png"
                alt="GestPub — Gestão Pública e Consultoria Autárquica"
                style={{ height: 40, width: 'auto', display: 'block', flexShrink: 0, mixBlendMode: 'multiply' }}
              />
            </Link>
            <div className="flex gap-3">
              <button
                onClick={handleShare}
                className="inline-flex items-center gap-2 px-4 py-2 transition-all duration-200"
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem', color: '#6B7280', borderRadius: '5px', border: '1px solid #E5E7EB', backgroundColor: '#FFFFFF', cursor: 'pointer' }}
                aria-label="Partilhar resultados"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="18" cy="5" r="3" />
                  <circle cx="6" cy="12" r="3" />
                  <circle cx="18" cy="19" r="3" />
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                </svg>
                <span className="hidden sm:inline">Partilhar</span>
              </button>
              <button
                onClick={() => window.print()}
                className="inline-flex items-center gap-2 px-4 py-2 transition-all duration-200"
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem', color: '#6B7280', borderRadius: '5px', border: '1px solid #E5E7EB', backgroundColor: '#FFFFFF', cursor: 'pointer' }}
                aria-label="Imprimir resultados"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <polyline points="6 9 6 2 18 2 18 9" />
                  <path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" />
                  <rect x="6" y="14" width="12" height="8" />
                </svg>
                <span className="hidden sm:inline">Imprimir</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Confirmation message — shown once after fresh submission */}
      {showConfirmation && (
        <div
          className="w-full"
          style={{ backgroundColor: '#ECFDF5', borderBottom: '1px solid #A7F3D0' }}
          role="status"
          aria-live="polite"
        >
          <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-3">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" className="flex-shrink-0" aria-hidden="true">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.875rem', color: '#065F46', fontWeight: 500 }}>
              Diagnóstico concluído com sucesso. Os seus resultados estão disponíveis abaixo.
            </p>
          </div>
        </div>
      )}

      {/* Hero result banner — matches reference dark hero */}
      <section
        className="relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #1a1a1a 0%, #272727 40%, #1e2e2b 70%, #1a2e28 100%)',
          paddingTop: '2.5rem',
          paddingBottom: '3rem',
        }}
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at 70% 50%, rgba(0,150,136,0.12) 0%, transparent 65%)' }}
        />
        {/* Top accent line */}
        <div
          className="absolute top-0 left-0 right-0 h-0.5"
          style={{ background: 'linear-gradient(90deg, transparent 0%, #009688 40%, #00bfa5 60%, transparent 100%)' }}
        />
        <div className="relative max-w-6xl mx-auto px-6">
          {leadName && (
            <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem', color: 'rgba(255,255,255,0.6)', marginBottom: '0.5rem' }}>
              Diagnóstico de {leadName}{municipality ? `, Município de ${municipality}` : ''}
            </p>
          )}
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="h-px w-8" style={{ backgroundColor: '#009688' }} />
                <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#009688' }}>
                  Perfil Organizacional, Serviço GP Otimiza da GestPub
                </span>
              </div>
              <h1
                className="text-white mb-3"
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '2rem', fontWeight: 700, lineHeight: 1.2 }}
              >
                {profile.name}
              </h1>
              <span
                className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white"
                style={{ borderRadius: '5px', backgroundColor: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)' }}
              >
                {profile.levelLabel}
              </span>
            </div>
            {/* Maturity score circle */}
            <div
              className="flex flex-col items-center p-6"
              style={{ borderRadius: '5px', backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', minWidth: '140px' }}
            >
              <div className="relative w-24 h-24">
                <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                  <circle cx="60" cy="60" r="50" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="10" />
                  <circle
                    cx="60" cy="60" r="50" fill="none"
                    stroke="#009688" strokeWidth="10"
                    strokeDasharray={`${(maturityPercent / 100) * 314} 314`}
                    strokeLinecap="round"
                    style={{ transition: 'stroke-dasharray 1s ease' }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '1.5rem', fontWeight: 700, color: '#FFFFFF' }}>{maturityPercent}</span>
                  <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.68rem', color: 'rgba(255,255,255,0.6)' }}>/ 100</span>
                </div>
              </div>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)', marginTop: '0.5rem', textAlign: 'center' }}>
                Índice de Maturidade
              </p>
            </div>
          </div>

          {showShareToast && (
            <div
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-white text-sm"
              style={{ borderRadius: '5px', backgroundColor: '#009688' }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Ligação copiada para a área de transferência
            </div>
          )}
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 py-10 space-y-6">
        {/* Profile interpretation — matches reference card style */}
        <div
          className="p-6"
          style={{ borderRadius: '5px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: `1px solid ${profile.borderColor}`, backgroundColor: profile.bgColor }}
        >
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-10 h-10 flex items-center justify-center"
              style={{ borderRadius: '5px', backgroundColor: '#FFFFFF', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={profile.color} strokeWidth="1.8" aria-hidden="true">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                <polyline points="9 22 9 12 15 12 15 22" />
              </svg>
            </div>
            <div>
              <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#6B7280' }}>
                Interpretação do Perfil
              </p>
              <h2 style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1.1rem', fontWeight: 700, color: profile.color }}>
                {profile.name}
              </h2>
            </div>
          </div>
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem', color: '#374151', lineHeight: 1.7 }}>
            {profile.interpretation}
          </p>
        </div>

        {/* Disclaimer note */}
        <div
          className="flex gap-4 p-5"
          style={{ borderRadius: '5px', backgroundColor: '#FFFBEB', border: '1px solid #FDE68A' }}
        >
          <div className="flex-shrink-0 mt-0.5">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          </div>
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.85rem', color: '#92400E', lineHeight: 1.65 }}>
            Este resultado não substitui uma análise técnica aprofundada. Trata-se de um diagnóstico preliminar destinado a identificar prioridades de melhoria e orientar uma eventual intervenção da GestPub no âmbito do GP Otimiza.
          </p>
        </div>

        {/* Area scores — matches reference card style */}
        <div
          className="bg-white p-6"
          style={{ borderRadius: '5px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: '1px solid #F3F4F6' }}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-4" style={{ backgroundColor: '#009688' }} />
            <h2 style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1.1rem', fontWeight: 700, color: '#111111' }}>
              Avaliação por Dimensão
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {AREA_NAMES.map((area, i) => {
              const score = Math.round(areaScores[i]);
              const isDominant = i === dominantAreaIndex;
              return (
                <div
                  key={area}
                  className="p-4"
                  style={{
                    borderRadius: '5px',
                    border: isDominant ? '1px solid #FECACA' : '1px solid #F3F4F6',
                    backgroundColor: isDominant ? '#FEF2F2' : '#FAFAFA',
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: isDominant ? '#DC2626' : '#009688' }}
                      />
                      <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.875rem', fontWeight: 600, color: '#374151' }}>
                        {area}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.875rem', fontWeight: 700, color: isDominant ? '#DC2626' : '#009688' }}>
                        {score}%
                      </span>
                      {isDominant && (
                        <span
                          className="ml-1 px-1.5 py-0.5 text-xs"
                          style={{ borderRadius: '3px', backgroundColor: '#FEE2E2', color: '#DC2626', fontFamily: 'DM Sans, sans-serif', fontWeight: 600 }}
                        >
                          Prioritário
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="w-full h-1.5 overflow-hidden" style={{ borderRadius: '9999px', backgroundColor: isDominant ? '#FECACA' : '#E5E7EB' }}>
                    <div
                      className="h-full"
                      style={{ width: `${score}%`, backgroundColor: isDominant ? '#DC2626' : '#009688', borderRadius: '9999px', transition: 'width 0.7s ease' }}
                    />
                  </div>
                  <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.75rem', color: '#9CA3AF', marginTop: '0.5rem' }}>
                    {AREA_DESCRIPTIONS[i]}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Priority recommendations — matches reference card style */}
        <div
          className="bg-white p-6"
          style={{ borderRadius: '5px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: '1px solid #F3F4F6' }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="h-px w-4" style={{ backgroundColor: '#009688' }} />
            <h2 style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1.1rem', fontWeight: 700, color: '#111111' }}>
              Recomendações Prioritárias de Intervenção
            </h2>
          </div>
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.82rem', color: '#9CA3AF', marginBottom: '1.5rem', paddingLeft: '1.25rem' }}>
            Baseadas nas dimensões com menor desempenho no seu diagnóstico
          </p>
          <div className="space-y-4">
            {recommendations.map((rec, i) => (
              <div
                key={i}
                className="flex gap-4 p-5 transition-shadow duration-200"
                style={{ borderRadius: '5px', border: '1px solid #F3F4F6', backgroundColor: '#FAFAFA' }}
                onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)')}
                onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'none')}
              >
                <div
                  className="flex-shrink-0 flex items-center justify-center w-8 h-8 text-white"
                  style={{ borderRadius: '5px', backgroundColor: '#009688', fontFamily: 'JetBrains Mono, monospace', fontSize: '0.8rem', fontWeight: 600 }}
                >
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 flex-wrap mb-1.5">
                    <h4 style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1rem', fontWeight: 700, color: '#111111' }}>
                      {rec.title}
                    </h4>
                    <span
                      className="flex-shrink-0 px-2 py-0.5 text-xs"
                      style={{
                        borderRadius: '3px',
                        fontFamily: 'DM Sans, sans-serif',
                        fontWeight: 600,
                        backgroundColor: rec.priority === 'alta' ? '#FEE2E2' : rec.priority === 'média' ? '#FEF3C7' : '#DCFCE7',
                        color: rec.priority === 'alta' ? '#DC2626' : rec.priority === 'média' ? '#D97706' : '#059669',
                      }}
                    >
                      Prioridade {rec.priority}
                    </span>
                  </div>
                  <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.875rem', color: '#6B7280', lineHeight: 1.6 }}>
                    {rec.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA — institutional dark section */}
        <section
          style={{ backgroundColor: '#272727', borderRadius: '5px', padding: '2.5rem 2rem' }}
        >
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-px w-8" style={{ backgroundColor: 'rgba(0,150,136,0.5)' }} />
              <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(0,150,136,0.8)' }}>
                Próximos Passos com a GestPub
              </span>
            </div>
            <h2
              className="text-white mb-4"
              style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1.75rem', fontWeight: 700, lineHeight: 1.25 }}
            >
              Do diagnóstico ao plano de intervenção
            </h2>
            <p
              className="mb-8"
              style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.95rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.65, maxWidth: '52ch' }}
            >
              Com base no perfil identificado, a GestPub pode apoiar o Município na definição de um plano de intervenção faseado, com medidas concretas para melhorar a organização interna, clarificar funções, simplificar processos e reforçar a capacidade de execução.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="mailto:geral@gestpub.pt?subject=Pedido%20de%20Reuni%C3%A3o%20de%20An%C3%A1lise%20-%20GP%20Otimiza"
                className="inline-flex items-center justify-center gap-2.5 bg-white text-gray-900 font-semibold text-sm transition-all duration-200 hover:bg-gray-50 hover:shadow-lg"
                style={{ fontFamily: 'DM Sans, sans-serif', borderRadius: '5px', border: '2px solid white', padding: '12px 28px' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                Agendar reunião de análise
              </a>
              <a
                href="mailto:geral@gestpub.pt?subject=Solicita%C3%A7%C3%A3o%20de%20Proposta%20de%20Interven%C3%A7%C3%A3o%20-%20GP%20Otimiza"
                className="inline-flex items-center justify-center gap-2.5 bg-transparent text-white font-medium text-sm transition-all duration-200 hover:bg-white/10"
                style={{ fontFamily: 'DM Sans, sans-serif', borderRadius: '5px', border: '1px solid rgba(255,255,255,0.3)', padding: '12px 28px' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
                Solicitar proposta de intervenção
              </a>
            </div>
            <p
              className="mt-6"
              style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem', color: 'rgba(255,255,255,0.45)' }}
            >
              Resposta em 24 horas úteis, com total confidencialidade.
            </p>
          </div>
        </section>

        {/* Maturity scale reference — matches reference card grid */}
        <div
          className="bg-white p-6"
          style={{ borderRadius: '5px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: '1px solid #F3F4F6' }}
        >
          <div className="flex items-center gap-3 mb-5">
            <div className="h-px w-4" style={{ backgroundColor: '#009688' }} />
            <h3 style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1rem', fontWeight: 700, color: '#111111' }}>
              Escala de Maturidade Organizacional
            </h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {Object.values(PROFILES).map((p) => {
              const isCurrentProfile = p.name === profile.name;
              return (
                <div
                  key={p.name}
                  className="p-4"
                  style={{
                    borderRadius: '5px',
                    border: isCurrentProfile ? `2px solid ${p.color}` : '1px solid #F3F4F6',
                    backgroundColor: isCurrentProfile ? p.bgColor : '#FAFAFA',
                  }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: p.color }}
                    />
                    <span style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.68rem', color: '#9CA3AF' }}>Nível {p.level}</span>
                    {isCurrentProfile && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="#009688" className="ml-auto" aria-hidden="true">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                  </div>
                  <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.875rem', fontWeight: 700, color: isCurrentProfile ? p.color : '#374151' }}>
                    {p.name}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Navigation */}
        <WorkflowNavigation
          currentPath="/results-page"
          onNext={() => {}}
          onBack={() => window.history.back()}
          nextLabel="Próximo"
          backLabel="Anterior"
        />

        {/* Footer note */}
        <div className="text-center pb-4">
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem', color: '#9CA3AF' }}>
            Diagnóstico realizado em {new Date().toLocaleDateString('pt-PT')} &bull; GestPub, GP Otimiza &copy; {new Date().getFullYear()} &bull; Todos os direitos reservados
          </p>
        </div>
      </div>

      {/* Footer — matches reference dark footer */}
      <footer style={{ backgroundColor: '#272727' }}>
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem', color: 'rgba(255,255,255,0.35)' }}>
            &copy; {new Date().getFullYear()} GestPub – Gestão Pública e Consultoria Autárquica. Todos os direitos reservados.
          </p>
          <div className="flex gap-6">
            {[
              { label: 'Política de Privacidade', href: '/politica-de-privacidade' },
              { label: 'Termos de Utilização', href: '/termos-de-utilizacao' },
              { label: 'RGPD', href: '/rgpd' },
            ]?.map((link) => (
              <Link
                key={link?.label}
                href={link?.href}
                className="transition-colors duration-200"
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem', color: 'rgba(255,255,255,0.35)' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#FFFFFF')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.35)')}
              >
                {link?.label}
              </Link>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}